import { useState, useEffect } from 'react';

/**
 * Custom hook that reports whether a CSS media query currently matches.
 *
 * Reads the initial value synchronously in the useState initializer so the
 * first render is correct and there is no flash of content that should be
 * hidden at the current viewport.
 *
 * @param {string} query - A CSS media query, e.g. '(max-width: 600px)'
 * @returns {boolean} Whether the query currently matches
 *
 * @example
 * const isPhone = useMediaQuery('(max-width: 600px)');
 */
const getInitialMatch = (query) =>
  typeof window !== 'undefined' && window.matchMedia(query).matches;

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => getInitialMatch(query));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia(query);
    const handler = (event) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

export default useMediaQuery;
